// src/pages/Product/InverterShowCase.jsx
import React, { useEffect, useRef, useState } from "react";
import "./InverterShowCase.css";

/* ── Feature definitions ── */
const LEFT_FEATURES = [
  {
    id: "psw",
    icon: "〰️",
    title: "Pure Sine Wave Output",
    desc: "Grid-identical waveform. Fans run silently, ACs start smoothly, routers never reset.",
  },
  {
    id: "dsp",
    icon: "🧠",
    title: "Advanced DSP Control",
    desc: "Digital Signal Processor manages power routing, charging profiles, and thermal load in real time.",
  },
  {
    id: "transfer",
    icon: "⚡",
    title: "< 10ms Transfer Time",
    desc: "Instantaneous switchover. Your computer, router, and appliances never see a flicker.",
  },
];

const RIGHT_FEATURES = [
  {
    id: "lithium",
    icon: "🔋",
    title: "Dedicated Lithium Profile",
    desc: "Optimised charging algorithm extends your Zynvert battery life by up to 40% over generic chargers.",
  },
  {
    id: "thermal",
    icon: "🌀",
    title: "Smart Thermal Management",
    desc: "Thermostatically controlled fan only spins when needed — whisper quiet during light loads.",
  },
  {
    id: "protect",
    icon: "🛡️",
    title: "5-Layer Protection Suite",
    desc: "Overload, short circuit, deep discharge, over-temp, and reverse polarity — all handled automatically.",
  },
];

const InverterShowcase = ({ productImage, specs, isIntegrated }) => {
  const rootRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);
  const [activeStat, setActiveStat] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 },
    );
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / rect.width;
      const dy = (e.clientY - cy) / rect.height;
      const img = el.querySelector(".inv-product-img");
      const glow = el.querySelector(".inv-core-glow");
      if (img)
        img.style.transform = `translate(${dx * 10}px, ${dy * 8}px) scale(1.02)`;
      if (glow) glow.style.transform = `translate(${dx * 18}px, ${dy * 12}px)`;
    };
    const onLeave = () => {
      const img = el.querySelector(".inv-product-img");
      const glow = el.querySelector(".inv-core-glow");
      if (img) img.style.transform = "";
      if (glow) glow.style.transform = "";
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  /* Pull live spec values from parsed JSON */
  const elec =
    specs?.electrical_specifications || specs?.inverter_specifications || {};
  const battCompat = specs?.battery_compatibility || {};

  const power = elec.rated_power_w ? `${elec.rated_power_w}W` : "1200W";
  const efficiency = elec.peak_efficiency_percent
    ? `${elec.peak_efficiency_percent}%`
    : "95%";
    
  // Fixed logic to prevent double formatting like "<< 10msms"
  const transfer = elec.transfer_time_ms
    ? elec.transfer_time_ms 
    : "<10ms";
    
  const surge = elec.surge_power_w ? `${elec.surge_power_w}W` : "2400W";
  const warranty = specs?.warranty?.duration_years
    ? `${specs.warranty.duration_years}Y`
    : "2Y";

  /* Animated "live" power flow display */
  const displayStats = [
    { label: "Rated Power", value: power },
    { label: "Peak Efficiency", value: efficiency },
    { label: "Transfer Time", value: transfer },
    { label: "Surge Power", value: surge },
  ];

  /* Cycle through stats automatically */
  useEffect(() => {
    if (!visible) return;
    const t = setInterval(
      () => setActiveStat((s) => (s + 1) % displayStats.length),
      2200,
    );
    return () => clearInterval(t);
  }, [visible, displayStats.length]);

  const imgSrc =
    productImage || "https://placehold.co/500x600/0a0a0a/222?text=Inverter";

  return (
    <section
      className={`inv-root${visible ? " inv-visible" : ""}`}
      ref={rootRef}
    >
      {/* BG */}
      <div className="inv-bg">
        <div className="inv-bg-grid" />
        <div className="inv-bg-vignette" />
        <div className="inv-bg-scanline" />
      </div>

      {/* Eyebrow */}
      <div className="inv-eyebrow">
        <span className="inv-eyebrow-dot" />
        {isIntegrated
          ? "ALL-IN-ONE POWER SOLUTION"
          : "PURE SINE WAVE TECHNOLOGY"}
      </div>

      {/* Headline */}
      <h2 className="inv-headline">
        {isIntegrated ? (
          <>
            One box.
            <br />
            <em>Total power.</em>
          </>
        ) : (
          <>
            Grid quality.
            <br />
            <em>On your terms.</em>
          </>
        )}
      </h2>

      {/* Three-column layout */}
      <div className="inv-stage">
        {/* LEFT features */}
        <div className="inv-col inv-col-left">
          {LEFT_FEATURES.map((f, i) => (
            <InvFeatureCard
              key={f.id}
              feature={f}
              delay={i * 100}
              align="right"
              active={activeFeature === f.id}
              onHover={setActiveFeature}
            />
          ))}
        </div>

        {/* CENTER — product + live display */}
        <div className="inv-center">
          <div className="inv-core-glow" />
          <div className="inv-ring inv-ring-1" />
          <div className="inv-ring inv-ring-2" />

          {/* Live "display" ticker floating above image */}
          <div className="inv-live-display">
            <div className="inv-display-bar" />
            {displayStats.map((s, i) => (
              <div
                key={s.label}
                className={`inv-display-stat${activeStat === i ? " inv-display-stat--active" : ""}`}
              >
                <span className="inv-display-val">{s.value}</span>
                <span className="inv-display-lbl">{s.label}</span>
              </div>
            ))}
            <div className="inv-display-indicator">
              {displayStats.map((_, i) => (
                <span
                  key={i}
                  className={`inv-disp-dot${activeStat === i ? " inv-disp-dot--on" : ""}`}
                />
              ))}
            </div>
          </div>

          <img
            src={imgSrc}
            alt="Zynvert Inverter"
            className="inv-product-img"
            onError={(e) =>
              (e.target.src =
                "https://placehold.co/400x500/0a0a0a/333?text=Inverter")
            }
          />

          {/* Transfer badge */}
          <div className="inv-badge inv-badge-bottom">
            <span className="inv-badge-dot inv-badge-dot--blue" />
            <span className="inv-badge-text">Auto-Switch Active</span>
          </div>
        </div>

        {/* RIGHT features */}
        <div className="inv-col inv-col-right">
          {RIGHT_FEATURES.map((f, i) => (
            <InvFeatureCard
              key={f.id}
              feature={f}
              delay={i * 100 + 150}
              align="left"
              active={activeFeature === f.id}
              onHover={setActiveFeature}
            />
          ))}
        </div>
      </div>

      {/* Spec strip */}
      <div className="inv-spec-strip">
        {[
          [power, "Rated Power"],
          [efficiency, "Peak Efficiency"],
          [transfer, "Transfer Time"],
          [surge, "Surge Capacity"],
          [warranty, "Warranty"],
        ].map(([val, label]) => (
          <div className="inv-spec" key={label}>
            <span className="inv-spec-val">{val}</span>
            <span className="inv-spec-label">{label}</span>
          </div>
        ))}
      </div>

      {/* Compatible battery types tag strip (only for standalone inverters) */}
      {!isIntegrated && battCompat.supported_chemistries?.length > 0 && (
        <div className="inv-compat">
          <span className="inv-compat-label">Compatible with:</span>
          {battCompat.supported_chemistries.map((c) => (
            <span className="inv-compat-tag" key={c}>
              {c}
            </span>
          ))}
        </div>
      )}

      <p className="inv-footnote">
        * All specifications reflect tested performance under standard
        conditions. Actual output may vary with load type.
      </p>
    </section>
  );
};

const InvFeatureCard = ({ feature, delay, align, active, onHover }) => (
  <div
    className={`inv-feat inv-feat-${align}${active ? " inv-feat-active" : ""}`}
    style={{ transitionDelay: `${delay}ms` }}
    onMouseEnter={() => onHover(feature.id)}
    onMouseLeave={() => onHover(null)}
  >
    <div className="inv-feat-icon-wrap">
      <span className="inv-feat-icon">{feature.icon}</span>
    </div>
    <div className="inv-feat-body">
      <p className="inv-feat-title">{feature.title}</p>
      <p className="inv-feat-desc">{feature.desc}</p>
    </div>
  </div>
);

export default InverterShowcase;